import { introJs } from 'intro.js';
import CookieService from 'services/CookieService';
import React from 'react';

export type IntroStep = { element?: string | Element; intro?: string };

/** Wait for an array of CSS selectors to be present in the DOM (polling) */
export const waitForSelectors = (selectors: string[] = [], timeout = 3000): Promise<boolean> => {
  const start = Date.now();
  return new Promise((resolve) => {
    const tick = () => {
      const allPresent = selectors.every((s) => !!document.querySelector(s));
      if (allPresent) return resolve(true);
      if (Date.now() - start > timeout) return resolve(false);
      setTimeout(tick, 150);
    };
    tick();
  });
};

export interface StartIntroOpts {
  timeout?: number;
  introOptions?: Record<string, any>;
  cookieKey?: string;
  cookieExpireDays?: number;
};

/**
 * Start an intro.js tour safely by waiting for selectors and filtering out
 * steps whose elements are missing. Returns a Promise that resolves to the
 * intro instance (or null if nothing was started).
 */
export async function startIntroTour(originalSteps: IntroStep[] = [], opts: StartIntroOpts = {}): Promise<any | null> {
  const { timeout = 3000, introOptions = {} } = opts;
  try {
    const cookieKey = opts.cookieKey;
    if (cookieKey) {
      const cs = new CookieService();
      const seen = cs.getCookie(cookieKey);
      if (seen) return null;
    }

  const intro = introJs();
    const selectors = (originalSteps || [])
      .map((s) => s.element)
      .filter(Boolean)
      .filter((x) => typeof x === 'string') as string[];

    await waitForSelectors(selectors, timeout);

    const safeSteps = (originalSteps || []).filter((s) => {
      if (!s.element) return true;
      if (typeof s.element === 'string') return !!document.querySelector(s.element);
      return s.element instanceof Element;
    });

    if (!safeSteps || safeSteps.length === 0) return null;

    // Start the intro with the safe steps. Keep behavior simple: allow callers
    // to pass `introOptions` to configure scrolling/positions if they want to.
    intro.setOptions({ skipLabel: 'Never Show For This Browser', steps: safeSteps, ...introOptions });
    intro.start();

    if (cookieKey) {
      const cs = new CookieService();
      const markSeen = () => cs.setCookie({ name: cookieKey, value: '1' }, opts.cookieExpireDays || 365);
      try {
        // intro.js callback APIs vary by version; attempt common handlers
        if (typeof (intro as any).oncomplete === 'function') (intro as any).oncomplete(markSeen);
        if (typeof (intro as any).onexit === 'function') (intro as any).onexit(markSeen);
      } catch (e) {
        try { (intro as any).on?.('complete', markSeen); (intro as any).on?.('exit', markSeen); } catch (e2) {}
      }
    }

    return intro;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('startIntroTour error', e);
    return null;
  }
}

/** Hook wrapper for functional components; optional and lightweight. */
export default function useIntroTour(originalSteps: IntroStep[] = [], opts: StartIntroOpts = {}) {
  try {
    const { useEffect } = React;
    useEffect(() => {
      let introInstance: any = null;
      (async () => {
        introInstance = await startIntroTour(originalSteps, opts);
      })();
      return () => {
        if (introInstance && typeof introInstance.exit === 'function') {
          introInstance.exit();
        }
      };
    }, [JSON.stringify(originalSteps)]);
  } catch (e) {
    // noop for non-react usage
  }
}

// Cookie helpers to query/set the intro seen flag manually
export function hasSeenIntro(cookieKey?: string): boolean {
  if (!cookieKey) return false;
  try {
    const cs = new CookieService();
    return !!cs.getCookie(cookieKey);
  } catch (e) { return false; }
}

export function setIntroSeen(cookieKey?: string, days = 365): void {
  if (!cookieKey) return;
  try {
    const cs = new CookieService();
    cs.setCookie({ name: cookieKey, value: '1' }, days);
  } catch (e) {}
}
